import { Config } from "../model/Config.js";
import { VOGateEntry } from "../model/DataStructure.js";
import { Gate } from "../apps/Gate.js";
import { Assets } from "../model/Assets.js";

export class GateItem {
    public onMatchChanged = e5.Signal.create<[GateItem, boolean]>();
    public onDragStart = e5.Signal.create<[GateItem]>();
    public onClickContent = e5.Signal.create<[GateItem]>();

    public control: HTMLDivElement;
    public content: HTMLDivElement;
    public dropped: HTMLDivElement;
    public animate: HTMLDivElement;

    private _offX: number;
    private _offY: number;
    private _matched: boolean = false;
    private _running: boolean = false;

    private _animation: gsap.core.Timeline;

    constructor(public data: VOGateEntry, gate: Gate, offX: number, offY: number) {
        this._offX = offX;
        this._offY = offY;

        var control = document.createElement("div");
        control.className = "gate-item-control";
        control.style.width = data.gate_image_default.width + "px";
        control.style.height = data.gate_image_default.height + "px";
        control.style.transform = "translate(" + offX + "px, " + offY + "px)";
        this.control = control;

        var controlInner = document.createElement("div");
        controlInner.classList.add("gate-item-control-inner");
        controlInner.style.backgroundImage = "url(" + data.gate_image_default.url + ")";
        controlInner.style.animationDelay = (Math.random() * 3) + "s";
        this.control.appendChild(controlInner);

        var animate = document.createElement("div");
        animate.className = "gate-item-animate";
        animate.style.width = data.gate_image_default.width + "px";
        animate.style.height = data.gate_image_default.height + "px";
        animate.style.transform = "translate(" + offX + "px, " + offY + "px)";
        animate.style.backgroundImage = "url(" + data.gate_image_default.url + ")";
        this.animate = animate;

        var handCursor = document.createElement("img");
        handCursor.src = Assets.getUrl("gate-hand-cursor");
        handCursor.classList.add("hand-cursor");
        animate.appendChild(handCursor);

        this._animation = gsap.timeline({ repeat: -1 });
        this._animation.add(gsap.to(this.animate, { duration: 1, opacity: 1 }));
        this._animation.add(gsap.to(this.animate, { duration: 3, x: data.gate_position_x, y: data.gate_position_y }));
        this._animation.add(gsap.to(this.animate, { duration: 1, opacity: 0 }));
        this._animation.pause();

        var content = document.createElement("div");
        content.className = "gate-item-content";
        content.style.width = data.gate_image_default.width + "px";
        content.style.height = data.gate_image_default.height + "px";
        content.style.backgroundImage = "url(" + data.gate_image_placeholder.url + ")";
        content.style.left = data.gate_position_x + "px";
        content.style.top = data.gate_position_y + "px";
        this.content = content;

        var dropped = document.createElement("div");
        dropped.className = "gate-item-dropped";
        dropped.style.width = data.gate_image_default.width + "px";
        dropped.style.height = data.gate_image_default.height + "px";
        dropped.style.backgroundImage = "url(" + data.gate_image_dropped.url + ")";
        dropped.style.left = data.gate_position_x + "px";
        dropped.style.top = data.gate_position_y + "px";
        this.dropped = dropped;

        var px = 0;
        var py = 0;
        var pointerId = -1;
        var pageX: number = 0;
        var pageY: number = 0;

        var onDown = (e: PointerEvent): void => {
            if (pointerId != -1 || this._matched)
                return;
            e.preventDefault();
            pointerId = e.pointerId;
            var bounds = control.getBoundingClientRect();
            var localX = e.pageX - bounds.left;
            var localY = e.pageY - bounds.top;
            px = localX;
            py = localY;

            pageX = e.pageX;
            pageY = e.pageY;

            this.onDragStart.dispatch(this);
            control.classList.add("dragging");
        };

        var onMove = (e: PointerEvent): void => {
            if (e.pointerId != pointerId || this._matched)
                return;

            var containerBounds = gate.itemContainer.getBoundingClientRect();
            var posX = e.pageX - containerBounds.left - px;
            var posY = e.pageY - containerBounds.top - py;
            posX *= 1 / gate.scale;
            posY *= 1 / gate.scale;
            control.style.transform = "translate(" + posX + "px, " + posY + "px)";

            pageX = e.pageX;
            pageY = e.pageY;
        };

        var onUp = (e: PointerEvent): void => {
            if (e.pointerId != pointerId || this._matched)
                return;
            pointerId = -1;

            control.classList.remove("dragging");

            var containerBounds = gate.itemContainer.getBoundingClientRect();
            var posX = pageX - containerBounds.left - px;
            var posY = pageY - containerBounds.top - py;
            posX *= 1 / gate.scale;
            posY *= 1 / gate.scale;

            var dx = posX - data.gate_position_x;
            var dy = posY - data.gate_position_y;
            var distance = Math.sqrt(dx * dx + dy * dy);
            var matched = distance < Config.dropTolerance;
            this.setMatched(matched);
        };

        control.addEventListener("pointerdown", onDown);
        dropped.addEventListener("pointerdown", () => {
            if (this.matched)
                this.onClickContent.dispatch(this);
        });
        document.body.addEventListener("pointermove", onMove);
        document.body.addEventListener("pointerup", onUp);
        document.body.addEventListener("pointercancel", onUp);
    }

    public get running(): boolean {
        return this._running;
    }

    public set running(running: boolean) {
        if (this._running == running)
            return;

        this.animate.classList.toggle("active", running);
        this.control.classList.toggle("invisible", running);
        if (running) {
            this._animation.restart();
        }
        else {
            this._animation.pause();
        }
        this._running = running;
    }

    public get matched(): boolean {
        return this._matched;
    }

    public set matched(matched: boolean) {
        if (this._matched == matched)
            return;
        this.setMatched(matched);
    }

    public setMatched(matched: boolean, tween: boolean = true): void {
        if (this._matched != matched) {
            this._matched = matched;
            this.onMatchChanged.dispatch(this, matched);
        }

        if (!tween) {
            this.control.classList.add("dragging");
            window.getComputedStyle(this.control).opacity;
        }

        if (matched) {
            this.content.classList.add("matched");
            this.dropped.classList.add("matched");
            this.control.classList.add("matched");
            this.control.style.transform = "translate(" + this.data.gate_position_x + "px, " + this.data.gate_position_y + "px)";
        }
        else {
            this.content.classList.remove("matched");
            this.dropped.classList.remove("matched");
            this.control.classList.remove("matched");
            this.control.style.transform = "translate(" + this._offX + "px, " + this._offY + "px)";
        }

        if (!tween) {
            window.getComputedStyle(this.control).opacity;
            this.control.classList.remove("dragging");
        }
    }
}